import { useCallback, useMemo } from 'react';

import { prepareEmailHtml } from '@lib/core/src/utils/htmlEmail';

import {
  useGetEmailThread,
  useGenerateEmailMessage,
  useGenerateDelegateFollowUpMessage,
  useGenerateEmailRespond,
  useGetEmailThreadMessages,
  useSendEmailMessage,
  SendEmailRequest,
  useGetEmailMessage,
  GenerateEmailMessageRequest,
  DelegateFollowUpRequestDto,
  EmailRespondRequestDto
} from '@/api/generated';

const useEmail = (threadId?: string) => {
  const {
    data: emailThreadResponse,
    isLoading: emailThreadLoading,
    isError: emailThreadError
  } = useGetEmailThread(threadId!, {
    query: {
      queryKey: ['getEmailThread', threadId],
      enabled: !!threadId
    }
  });

  const emailThread = useMemo(
    () => emailThreadResponse?.items?.[0] || undefined,
    [emailThreadResponse?.items]
  );

  const {
    data: emailThreadMessagesResponse,
    isLoading: emailThreadMessagesLoading,
    isError: emailThreadMessagesError
  } = useGetEmailThreadMessages(threadId!, {
    query: {
      queryKey: ['getEmailThreadMessages', threadId],
      enabled: !!threadId
    }
  });

  const emailThreadMessages = useMemo(
    () => emailThreadMessagesResponse?.items || [],
    [emailThreadMessagesResponse?.items]
  );

  const {
    data: emailMessageResponse,
    isLoading: emailMessageLoading,
    isError: emailMessageError
  } = useGetEmailMessage(threadId!, {
    query: {
      queryKey: ['getEmailMessage', threadId],
      enabled: !!threadId
    }
  });

  const emailMessage = useMemo(
    () => emailMessageResponse?.items?.[0] || undefined,
    [emailMessageResponse?.items]
  );

  const {
    mutateAsync: mutateGenerateEmailMessage,
    isPending: isGenerateEmailMessagePending,
    isError: isGenerateEmailMessageError
  } = useGenerateEmailMessage();

  const {
    mutateAsync: mutateGenerateDelegateFollowUpMessage,
    isPending: isGenerateDelegateFollowUpMessagePending,
    isError: isGenerateDelegateFollowUpMessageError
  } = useGenerateDelegateFollowUpMessage();

  const {
    mutateAsync: mutateGenerateEmailRespond,
    isPending: isGenerateEmailRespondPending,
    isError: isGenerateEmailRespondError
  } = useGenerateEmailRespond();

  const { mutateAsync: mutateSendEmailMessage } = useSendEmailMessage();

  const generateEmailMessage = useCallback(
    async (data: GenerateEmailMessageRequest) => {
      const response = await mutateGenerateEmailMessage({ data });
      return response?.items?.[0];
    },
    [mutateGenerateEmailMessage]
  );

  const sendEmailMessage = useCallback(
    async (request: SendEmailRequest) => {
      const response = await mutateSendEmailMessage({ data: request });
      return response?.items?.[0];
    },
    [mutateSendEmailMessage]
  );

  const generateDelegateFollowUpMessage = useCallback(
    async (data: DelegateFollowUpRequestDto) => {
      const response = await mutateGenerateDelegateFollowUpMessage({ data });
      return response?.items?.[0];
    },
    [mutateGenerateDelegateFollowUpMessage]
  );

  const generateEmailRespond = useCallback(
    async (data: EmailRespondRequestDto) => {
      const response = await mutateGenerateEmailRespond({ data });
      return response?.items?.[0];
    },
    [mutateGenerateEmailRespond]
  );

  const getThreadContent = useCallback(() => {
    if (emailThreadMessages.length > 0) {
      return emailThreadMessages.map(message => message.conversation).join('\n');
    }
    return '';
  }, [emailThreadMessages]);

  const getLastEmailContent = useCallback(() => {
    let emailContent = '';
    if (emailThreadMessages && emailThreadMessages.length > 0) {
      let messageIndex = 0;
      for (let i = 0; i < emailThreadMessages.length; i++) {
        const currentDate = emailThreadMessages[i]?.date;
        const messageDate = emailThreadMessages[messageIndex]?.date;

        if (currentDate && messageDate && currentDate > messageDate) {
          messageIndex = i;
        }
      }
      emailContent = emailThreadMessages[messageIndex]?.conversation || '';
    }
    return emailContent;
  }, [emailThreadMessages]);

  const getLastEmailBody = useCallback(() => {
    if (!emailThreadMessages) return '';
    return prepareEmailHtml(emailThreadMessages[emailThreadMessages.length - 1]?.body || '');
  }, [emailThreadMessages]);

  const getLastEmailSender = useCallback(() => {
    if (!emailThreadMessages) return null;
    return emailThreadMessages[emailThreadMessages.length - 1]?.from?.[0];
  }, [emailThreadMessages]);

  return {
    emailThread,
    emailThreadLoading,
    emailMessage,
    emailMessageLoading,
    generateEmailMessage,
    isGenerateEmailMessagePending,
    sendEmailMessage,
    isGenerateEmailMessageError,
    emailThreadError,
    emailMessageError,
    generateDelegateFollowUpMessage,
    isGenerateDelegateFollowUpMessagePending,
    isGenerateDelegateFollowUpMessageError,
    generateEmailRespond,
    isGenerateEmailRespondPending,
    isGenerateEmailRespondError,
    emailThreadMessages,
    emailThreadMessagesLoading,
    emailThreadMessagesError,
    getThreadContent,
    getLastEmailContent,
    getLastEmailSender,
    getLastEmailBody
  };
};

export default useEmail;
